import React, { useState } from 'react';
import { 
  Typography, 
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Alert,
  Stack,
  Fade,
  IconButton,
  Tooltip
} from '@mui/material';
import InsightCard from './shared/InsightCard';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { mockGenerateProfileSummary } from '../../services/mockAIService';

interface AIProfileSummaryCardProps {
  customerId: number;
}

interface AIProfileData {
  customer_type: string;
  behavioral_patterns: string[];
  recommended_services: string[];
  service_notes: string[];
  generated_at: string;
  expires_at: string;
  is_cached: boolean;
}

const AIProfileSummaryCard: React.FC<AIProfileSummaryCardProps> = ({ customerId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AIProfileData | null>(null);
  const [isGenerated, setIsGenerated] = useState(false);

  const handleGenerateAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await mockGenerateProfileSummary(customerId);
      setData(result);
      setIsGenerated(true);
    } catch (err) {
      setError('AI分析に失敗しました。もう一度お試しください。');
      setIsGenerated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = () => {
    setData(null);
    setIsGenerated(false);
    handleGenerateAnalysis();
  };

  const formatExpiryTime = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffInDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return `${diffInDays}日間有効`;
  };

  const getCustomerTypeColor = (type: string) => {
    switch (type) {
      case 'VIP':
        return { bg: '#fff3e0', color: '#e65100' };
      case 'リピーター':
        return { bg: '#e8f5e9', color: '#2e7d32' };
      case '新規':
        return { bg: '#e3f2fd', color: '#0277bd' };
      default:
        return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  // 初期状態：分析ボタン表示
  if (!isGenerated && !loading) {
    return (
      <InsightCard
        title="AI顧客分析"
        cardVariant="warning"
      >
          
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              AIが顧客の特徴や行動パターンを分析し、
              <br />
              最適なサービス提案を生成します
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              startIcon={<PsychologyIcon />}
              onClick={handleGenerateAnalysis}
              sx={{
                bgcolor: 'warning.main',
                '&:hover': {
                  bgcolor: 'warning.dark'
                },
                borderRadius: 2,
                px: 4,
                py: 1.5
              }}
            >
              AIで分析
            </Button>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Chip
                icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                label="約3秒"
                size="small"
                variant="outlined"
                sx={{ borderColor: 'warning.300', color: 'warning.dark' }}
              />
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                label="GPT-5-mini"
                size="small"
                variant="outlined"
                sx={{ borderColor: 'warning.300', color: 'warning.dark' }}
              />
            </Box>
          </Box>
      </InsightCard>
    );
  }

  // ローディング状態
  if (loading) {
    return (
      <InsightCard
        title="AI顧客分析"
        cardVariant="warning"
      >
          
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress 
              size={48} 
              sx={{ color: 'warning.main', mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              分析中...
            </Typography>
            
            <Stack spacing={2}>
              <Skeleton variant="rectangular" height={32} sx={{ borderRadius: 1 }} />
              <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
              <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Skeleton variant="rounded" width={80} height={24} />
                <Skeleton variant="rounded" width={80} height={24} />
                <Skeleton variant="rounded" width={80} height={24} />
              </Box>
            </Stack>
          </Box>
      </InsightCard>
    );
  }

  // エラー状態
  if (error && !data) {
    return (
      <InsightCard
        title="AI顧客分析"
        cardVariant="warning"
      >
          
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            icon={<ErrorOutlineIcon />}
          >
            {error}
          </Alert>
          
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleGenerateAnalysis}
              sx={{
                borderColor: 'warning.main',
                color: 'warning.main',
                '&:hover': {
                  borderColor: 'warning.dark',
                  bgcolor: 'warning.50'
                }
              }}
            >
              再試行
            </Button>
          </Box>
      </InsightCard>
    );
  }

  // 結果表示状態
  if (data) {
    const typeColor = getCustomerTypeColor(data.customer_type);
    
    return (
      <InsightCard
        title="AI顧客分析"
        cardVariant="warning"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {data.is_cached && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                label={formatExpiryTime(data.expires_at)}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            
            <Tooltip title="再生成">
              <IconButton 
                size="small" 
                onClick={handleRegenerate}
                sx={{ color: 'warning.main' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      >

          <Fade in timeout={500} appear={false} mountOnEnter unmountOnExit={false}>
            <Box>
              {/* 顧客タイプ */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  顧客タイプ
                </Typography>
                <Chip
                  label={data.customer_type}
                  sx={{
                    bgcolor: typeColor.bg,
                    color: typeColor.color,
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    px: 2,
                    py: 0.5
                  }}
                />
              </Box>

              {/* 行動パターン */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  行動パターン
                </Typography>
                <Stack spacing={1}>
                  {data.behavioral_patterns.map((pattern, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'warning.main' 
                      }} />
                      <Typography variant="body2">
                        {pattern}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* おすすめサービス */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  おすすめサービス
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {data.recommended_services.map((service, index) => (
                    <Chip
                      key={index}
                      label={service}
                      size="small"
                      sx={{
                        bgcolor: 'warning.50',
                        color: 'warning.dark',
                        fontWeight: 500
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* サービスノート */}
              {data.service_notes && data.service_notes.length > 0 && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'warning.50', 
                  borderRadius: 1,
                  border: '1px solid rgba(255, 152, 0, 0.2)'
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    施術時の注意点
                  </Typography>
                  {data.service_notes.map((note, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                      • {note}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Fade>
      </InsightCard>
    );
  }

  return null;
};

export default AIProfileSummaryCard;